'use client';

import {
  createContext, useContext, useCallback, ReactNode, useMemo,
} from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { getI18nLinkHref } from '@services/i18nPaths';
import { slugify } from '@utils/strings/slugify';

export type CandidateSearchFilters = {
  keyword?: string;
  locations?: string[];
  salary?: number;
  originalKeyword?: string;
  decomposedFilters?: ApiSchema<'JobSearchRequestFilter'>;
};

export type SidebarView =
  | 'new-search'
  | 'new-job-search-form'
  | 'my-bestie-landing'
  | 'browse-all'
  | { keyword: string }
  | { candidateSearch: CandidateSearchFilters }
  | { editSearch: CandidateSearchFilters };

type NavigateOptions = { replace?: boolean };

interface SidebarNavigationContextValue {
  currentView: SidebarView;
  setView: (view: SidebarView, options?: NavigateOptions) => void;
  navigateToNewSearch: () => void;
  navigateToNewJobSearchForm: () => void;
  navigateToMyBestieLanding: () => void;
  navigateToBrowseAll: () => void;
  navigateToKeywordSearch: (keyword: string) => void;
  navigateToCandidateSearch: (filters: CandidateSearchFilters, options?: NavigateOptions) => void;
  navigateToEditSearch: (filters: CandidateSearchFilters) => void;
}

const SidebarNavigationContext = createContext<SidebarNavigationContextValue | undefined>(undefined);

interface SidebarNavigationProviderProps {
  children: ReactNode;
  /** SSR seed for the very first paint, before the client URL hooks resolve query params. The URL is the source of truth thereafter. */
  initialView?: SidebarView;
  locale: Locale;
}

/** Derives the sidebar view from a path + search string (used for popstate/back-forward). */
const getViewFromUrl = (path: string, search: string): SidebarView => {
  const urlParams = new URLSearchParams(search);
  const isEditMode = urlParams.get('edit') === '1';

  // Backward compat: old keyword-search URL — treat as candidateSearch
  const keywordSearchMatch = path.match(/\/mc\/job-search\/([^/]+)/);
  if (keywordSearchMatch) {
    const keyword = decodeURIComponent(keywordSearchMatch[1]).replace(/-/g, ' ');
    return { candidateSearch: { keyword } };
  }

  // Candidate search/edit results: /mc/locuri-de-munca[-in-CITY][/KEYWORD] or /mc/jobs[-in-CITY][/KEYWORD]
  const candidateListMatch = path.match(/\/mc\/(?:locuri-de-munca|jobs)(?:-in-([^/]+?))?(?:\/([^/?]+))?(?=[/?]|$)/);
  if (candidateListMatch) {
    const locationSlug = candidateListMatch[1];
    const keywordSlug = candidateListMatch[2];
    const keyword = keywordSlug ? decodeURIComponent(keywordSlug).replace(/-/g, ' ') : undefined;
    const locations = locationSlug ? locationSlug.split(',').map((s) => decodeURIComponent(s)) : undefined;
    const filters = {
      ...(keyword && { keyword }),
      ...(locations?.length && { locations }),
    };
    // ?edit=1 signals the edit form should be shown, not results
    return isEditMode ? { editSearch: filters } : { candidateSearch: filters };
  }

  if (/\/mc\/my-bestie(\/|$)/.test(path)) {
    return 'my-bestie-landing';
  }

  if (/\/mc\/bestie-chat(\/|$)/.test(path)) {
    return 'new-search';
  }

  if (/\/mc\/search/.test(path)) {
    const keyword = urlParams.get('keyword') ?? undefined;
    const locations = urlParams.getAll('location');
    if (keyword || locations.length) {
      return {
        editSearch: {
          ...(keyword && { keyword }),
          ...(locations.length && { locations }),
        },
      };
    }
    return 'new-job-search-form';
  }

  // Default: /mc and any unmatched MC path resolve to the search form so the
  // candidate's landing matches the page component's initialView.
  return 'new-job-search-form';
};

/**
 * @description Provider for sidebar navigation state management.
 *
 * View state is initialised from the `initialView` prop (set by the server page component
 * based on the current URL). Client-side navigation calls `setView` which updates state
 * immediately and then pushes the matching URL. Browser back/forward is handled via a
 * `popstate` listener so the effect cannot race with explicit `setView` calls.
 */
export const SidebarNavigationProvider = (props: SidebarNavigationProviderProps) => {
  const { children, locale, initialView = 'new-search' } = props;

  const { router } = useExtendedRouter();
  const pathname = usePathname() ?? '';
  const searchParams = useSearchParams();
  const search = searchParams?.toString() ?? '';

  // Single source of truth: the URL. The current view is derived from the path +
  // query, so soft navigation (setView -> router.push) drives the rendered content
  // and back/forward works for free — no parallel state machine to desync.
  // `initialView` only seeds the very first paint before the client URL hooks resolve.
  const currentView = useMemo(
    () => getViewFromUrl(pathname, search) || initialView,
    [pathname, search, initialView],
  );

  // Build the MC URL that represents a given sidebar view.
  const buildViewUrl = useCallback((view: SidebarView): string => {
    if (view === 'new-search') return getI18nLinkHref('/mc/bestie-chat', locale);
    if (view === 'browse-all') return getI18nLinkHref('/mc', locale);
    if (view === 'my-bestie-landing') return getI18nLinkHref('/mc/my-bestie', locale);
    // `/mc` is the canonical candidate landing and renders the search form,
    // so we point the bare form view there instead of `/mc/search`. The
    // `/mc/search` route still exists for deep links carrying `?keyword=…`
    // (which resolve to `editSearch` via getViewFromUrl).
    if (view === 'new-job-search-form') return getI18nLinkHref('/mc', locale);
    if (typeof view === 'object' && 'keyword' in view) {
      return getI18nLinkHref(`/mc/job/list/${slugify(view.keyword)}`, locale);
    }

    const isEdit = typeof view === 'object' && 'editSearch' in view;
    const filters = isEdit ? view.editSearch : (view).candidateSearch;
    const { keyword, locations } = filters;
    const keywordSlug = keyword ? `/${slugify(keyword)}` : '';

    const base = locations?.length
      ? getI18nLinkHref(`/mc/job/list/location-${locations.map(slugify).join(',')}`, locale).replace(/%2C/g, ',')
      : getI18nLinkHref('/mc/job/list', locale);

    return `${base}${keywordSlug}${isEdit ? '?edit=1' : ''}`;
  }, [locale]);

  // Navigate for real (soft, client-side). The view is derived from the resulting URL,
  // so the rendered content updates once the route changes — no manual state sync.
  // The `replace` option is used by post-fetch URL corrections (e.g. when /v2/jobs
  // `decomposedFilters` reveals the typed keyword was actually a location) so the
  // pre-correction URL doesn't bloat browser history.
  const setView = useCallback((view: SidebarView, options?: NavigateOptions) => {
    const url = buildViewUrl(view);
    if (options?.replace) {
      void router.replace(url, { withLocale: false });
    } else {
      void router.push(url, { withLocale: false });
    }
  }, [buildViewUrl, router]);

  // Convenience methods
  const navigateToNewSearch = useCallback(() => setView('new-search'), [setView]);
  const navigateToNewJobSearchForm = useCallback(() => setView('new-job-search-form'), [setView]);
  const navigateToMyBestieLanding = useCallback(() => setView('my-bestie-landing'), [setView]);
  const navigateToBrowseAll = useCallback(() => setView('browse-all'), [setView]);
  const navigateToKeywordSearch = useCallback((keyword: string) => setView({ keyword }), [setView]);
  const navigateToCandidateSearch = useCallback(
    (filters: CandidateSearchFilters, options?: NavigateOptions) => setView({ candidateSearch: filters }, options),
    [setView],
  );
  const navigateToEditSearch = useCallback((filters: CandidateSearchFilters) => setView({ editSearch: filters }), [setView]);

  const value: SidebarNavigationContextValue = useMemo(() => ({
    currentView,
    setView,
    navigateToNewSearch,
    navigateToNewJobSearchForm,
    navigateToMyBestieLanding,
    navigateToBrowseAll,
    navigateToKeywordSearch,
    navigateToCandidateSearch,
    navigateToEditSearch,
  }), [
    currentView, setView, navigateToNewSearch, navigateToNewJobSearchForm,
    navigateToMyBestieLanding, navigateToBrowseAll,
    navigateToKeywordSearch, navigateToCandidateSearch, navigateToEditSearch,
  ]);

  return (
    <SidebarNavigationContext.Provider value={value}>
      {children}
    </SidebarNavigationContext.Provider>
  );
};

/**
 * @description Hook to access sidebar navigation context.
 * Throws error if used outside of SidebarNavigationProvider.
 */
export const useSidebarNavigation = () => {
  const context = useContext(SidebarNavigationContext);

  if (!context) {
    throw new Error('useSidebarNavigation must be used within SidebarNavigationProvider');
  }

  return context;
};

/**
 * @description Optional variant used by components that can render both inside and
 * outside the SidebarNavigationProvider (e.g. standalone MC pages).
 */
export const useSidebarNavigationOptional = () => useContext(SidebarNavigationContext);
